import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import allProjectsData from '../data/projectsPageData';

import { motion, AnimatePresence } from 'framer-motion';

import {
    FaBed,
    FaBath,
    FaRulerCombined,
    FaParking,
    FaFire,
    FaMapMarkerAlt,
    FaCalendarAlt,
    FaCheckCircle,
    FaFilePdf, FaStore, FaChild, FaThermometerHalf, FaFireAlt, FaHome
} from 'react-icons/fa';
import {MdOutlineApartment, MdOutlineHolidayVillage, MdClose, MdElevator, MdWindow} from 'react-icons/md';
import { IoLocationSharp, IoCalendarOutline, IoCheckmarkCircleSharp, IoArrowBack, IoChevronBack, IoChevronForward } from 'react-icons/io5';

const iconMap = {
    FaBed, FaBath, FaRulerCombined, FaParking, FaFire, FaMapMarkerAlt, FaCalendarAlt, FaCheckCircle,
    MdOutlineApartment, MdOutlineHolidayVillage,
    FaThermometerHalf,
    MdWindow,
    MdElevator,
    FaChild,
    FaStore,
    FaFireAlt,
    FaFilePdf,
    FaHome
};

const variants = {
    enter: (direction) => ({ x: direction > 0 ? 100 : -100, opacity: 0, scale: 0.98 }),
    center: { zIndex: 1, x: 0, opacity: 1, scale: 1 },
    exit: (direction) => ({ zIndex: 0, x: direction < 0 ? 100 : -100, opacity: 0, scale: 0.98 }),
};

const AUTOPLAY_INTERVAL = 5000;

const ProjectDetailPage = () => {
    const { projectId } = useParams();
    const project = allProjectsData.find(p => String(p.id) === projectId || p.slug === projectId);

    const [activeIndex, setActiveIndex] = useState(0);
    const [direction, setDirection] = useState(0);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [autoplayActive, setAutoplayActive] = useState(true);
    const autoplayTimeoutRef = useRef(null);

    const nextSlide = useCallback(() => {
        if (!project || !project.gallery || project.gallery.length === 0) return;
        setDirection(1);
        setActiveIndex((prevIndex) =>
            prevIndex === project.gallery.length - 1 ? 0 : prevIndex + 1
        );
    }, [project]);

    const prevSlide = useCallback(() => {
        if (!project || !project.gallery || project.gallery.length === 0) return;
        setDirection(-1);
        setActiveIndex((prevIndex) =>
            prevIndex === 0 ? project.gallery.length - 1 : prevIndex - 1
        );
    }, [project]);

    useEffect(() => {
        if (autoplayTimeoutRef.current) clearTimeout(autoplayTimeoutRef.current);
        if (autoplayActive && project && project.gallery && project.gallery.length > 1) {
            autoplayTimeoutRef.current = setTimeout(nextSlide, AUTOPLAY_INTERVAL);
        }
        return () => { if (autoplayTimeoutRef.current) clearTimeout(autoplayTimeoutRef.current); };
    }, [activeIndex, autoplayActive, nextSlide, project]);

    const handleThumbnailClick = (index) => {
        setAutoplayActive(false);
        setDirection(index > activeIndex ? 1 : -1);
        setActiveIndex(index);
    };

    const handlePrevClick = () => {
        setAutoplayActive(false);
        prevSlide();
    };

    const handleNextClick = () => {
        setAutoplayActive(false);
        nextSlide();
    };

    const openLightbox = () => {
        if (!project || !project.gallery || project.gallery.length === 0) return;
        setAutoplayActive(false);
        setIsLightboxOpen(true);
    };

    const closeLightbox = () => {
        setIsLightboxOpen(false);
    };

    if (!project) {
        return <div className="container mx-auto px-4 py-40 text-center text-arcdom-dark-gray">Project not found. <Link to="/projects" className="text-arcdom-gold hover:underline">Return to projects list</Link></div>;
    }

    const hasGallery = project.gallery && project.gallery.length > 0;

    const mapQuery = project.address || project.location || '';
    const mapUrl = `https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&hl=en&z=14&ie=UTF8&iwloc=B&output=embed`;

    // Handle multiple categories for display
    const categoryDisplay = Array.isArray(project.category)
        ? project.category.join(' & ')
        : project.category;

    // Get status display and color
    const getStatusDisplay = (status) => {
        if (status === "Completed Projects") return { text: "Completed", color: "text-green-600" };
        if (status === "Work In Progress") return { text: "In Progress", color: "text-blue-600" };
        return { text: status || "Unknown", color: "text-yellow-600" };
    };

    const statusInfo = getStatusDisplay(project.status);

    return (
        <>
            <div className="bg-[#eceeF1] pt-24 pb-16 min-h-screen">
                <div className="container mx-auto px-4">

                    <div className="mb-6">
                        <Link
                            to="/projects"
                            className="inline-flex items-center px-4 py-2 border border-arcdom-taupe text-arcdom-taupe rounded-md font-medium hover:bg-arcdom-taupe hover:text-white transition-colors duration-300 text-sm"
                        >
                            <IoArrowBack className="mr-2" /> Back to All Projects
                        </Link>
                    </div>

                    <div className="mb-10 text-center border-b border-gray-300 pb-8">
                        <h1 className="text-3xl md:text-5xl font-bold text-arcdom-navy mb-4">{project.title}</h1>
                        <div className="flex flex-col items-center space-y-2 md:space-y-0 md:flex-row md:items-center md:justify-center md:space-x-5 text-arcdom-dark-gray text-base md:text-lg">
                             <span className="inline-flex items-center">
                                 <IoLocationSharp className="mr-2 text-arcdom-gold flex-shrink-0" /> {project.location}
                             </span>
                            <span className="inline-flex items-center">
                                 <IoCalendarOutline className="mr-2 text-arcdom-gold flex-shrink-0" /> {project.startDate || 'N/A'} - {project.endDate || 'N/A'}
                             </span>
                            <span className={`inline-flex items-center font-medium ${statusInfo.color}`}>
                                 <IoCheckmarkCircleSharp className="mr-2 flex-shrink-0" /> {statusInfo.text}
                             </span>
                        </div>
                        {/* Display category */}
                        <div className="mt-3 inline-flex items-center text-arcdom-dark-gray">
                            <span className="text-sm font-medium px-3 py-1 bg-arcdom-gold/10 rounded-full">
                                {categoryDisplay}
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                        <div className="lg:col-span-2 space-y-10">

                            {hasGallery && (
                                <div className="bg-white p-4 md:p-6 rounded-lg shadow relative">
                                    <div
                                        className="relative w-full aspect-[4/3] md:aspect-video overflow-hidden rounded-lg mb-4 bg-gray-200 cursor-pointer group"
                                        onClick={openLightbox}
                                    >
                                        <AnimatePresence initial={false} custom={direction}>
                                            <motion.img
                                                key={activeIndex}
                                                src={project.gallery[activeIndex]}
                                                alt={`${project.title} Image ${activeIndex + 1}`}
                                                custom={direction}
                                                variants={variants}
                                                initial="enter"
                                                animate="center"
                                                exit="exit"
                                                transition={{ x: { type: "spring", stiffness: 300, damping: 30 }, opacity: { duration: 0.3 }, scale: {duration: 0.3} }}
                                                className="absolute top-0 left-0 w-full h-full object-contain"
                                                draggable={false}
                                            />
                                        </AnimatePresence>

                                        {project.gallery.length > 1 && (
                                            <>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handlePrevClick(); }}
                                                    className="absolute top-1/2 left-3 transform -translate-y-1/2 z-10 bg-black bg-opacity-30 text-white hover:bg-opacity-60 rounded-full p-2 transition-all duration-300 opacity-0 group-hover:opacity-100 hidden md:block"
                                                    aria-label="Previous image"
                                                >
                                                    <IoChevronBack size={24} />
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleNextClick(); }}
                                                    className="absolute top-1/2 right-3 transform -translate-y-1/2 z-10 bg-black bg-opacity-30 text-white hover:bg-opacity-60 rounded-full p-2 transition-all duration-300 opacity-0 group-hover:opacity-100 hidden md:block"
                                                    aria-label="Next image"
                                                >
                                                    <IoChevronForward size={24} />
                                                </button>
                                            </>
                                        )}
                                    </div>

                                    {project.gallery.length > 1 && (
                                        <div className="flex space-x-2 overflow-x-auto pb-2 pt-2">
                                            {project.gallery.map((imgSrc, index) => (
                                                <motion.div
                                                    key={index}
                                                    onClick={() => handleThumbnailClick(index)}
                                                    className={`w-20 h-14 md:w-24 md:h-16 flex-shrink-0 rounded cursor-pointer overflow-hidden border-2 transition-colors duration-200 ${activeIndex === index ? 'border-arcdom-gold' : 'border-gray-400 hover:border-arcdom-taupe'}`}
                                                    whileHover={{ scale: 1.05 }}
                                                    transition={{ type: "spring", stiffness: 400, damping: 15 }}
                                                    animate={{ scale: activeIndex === index ? 1 : 0.95, opacity: activeIndex === index ? 1 : 0.7 }}
                                                >
                                                    <img src={imgSrc} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                                                </motion.div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="bg-white p-6 rounded-lg shadow">
                                <h2 className="text-2xl font-semibold text-arcdom-navy mb-4">Project Overview</h2>
                                <p className="text-gray-700 leading-relaxed whitespace-pre-line">{project.description || "No description available."}</p>
                            </div>

                            {project.configurations && project.configurations.length > 0 && (
                                <div className="bg-white p-6 rounded-lg shadow">
                                    <h2 className="text-2xl font-semibold text-arcdom-navy mb-4">Available Configurations</h2>
                                    <div className="space-y-3">
                                        {project.configurations.map((config, index) => (
                                            <div key={index} className="border-b border-gray-200 pb-2 last:border-b-0">
                                                <p className="font-semibold text-gray-800">{config.type}:</p>
                                                <p className="text-gray-600">{config.details}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="lg:col-span-1 space-y-10">
                            {project.features && project.features.length > 0 && (
                                <div className="bg-white p-6 rounded-lg shadow">
                                    <h2 className="text-2xl font-semibold text-arcdom-navy mb-5">Key Features</h2>
                                    <ul className="space-y-3">
                                        {project.features.map((feature, index) => {
                                            const IconComponent = iconMap[feature.icon];
                                            return (
                                                <li key={index} className="flex items-center text-gray-700">
                                                    {IconComponent ? <IconComponent className="w-5 h-5 mr-3 text-arcdom-gold flex-shrink-0" /> : <span className="w-5 h-5 mr-3"></span>}
                                                    {feature.text}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}

                            <div className="bg-white p-6 rounded-lg shadow">
                                <h2 className="text-2xl font-semibold text-arcdom-navy mb-5">Location</h2>
                                <div className="overflow-hidden rounded-lg border border-gray-300" style={{ height: '350px' }}>
                                    <iframe
                                        src={mapUrl}
                                        width="100%"
                                        height="100%"
                                        style={{ border: 0 }}
                                        allowFullScreen={false}
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                        title={`${project.title} Location Map`}
                                    ></iframe>
                                </div>
                                <p className="text-sm text-gray-600 mt-3">Address: {project.address || project.location || 'Not specified'}</p>
                            </div>

                            {/* Project Status Information */}
                            <div className="bg-white p-6 rounded-lg shadow">
                                <h2 className="text-2xl font-semibold text-arcdom-navy mb-5">Project Status</h2>
                                <ul className="space-y-3">
                                    <li className="flex items-center text-gray-700">
                                        <FaCalendarAlt className="w-5 h-5 mr-3 text-arcdom-gold flex-shrink-0" />
                                        <span className="font-medium">Year: </span>
                                        <span className="ml-2">{project.year}</span>
                                    </li>
                                    <li className={`flex items-center ${statusInfo.color}`}>
                                        <IoCheckmarkCircleSharp className="w-5 h-5 mr-3 flex-shrink-0" />
                                        <span className="font-medium">Status: </span>
                                        <span className="ml-2">{statusInfo.text}</span>
                                    </li>
                                    {project.startDate && (
                                        <li className="flex items-center text-gray-700">
                                            <FaCalendarAlt className="w-5 h-5 mr-3 text-arcdom-gold flex-shrink-0" />
                                            <span className="font-medium">Start Date: </span>
                                            <span className="ml-2">{project.startDate}</span>
                                        </li>
                                    )}
                                    {project.endDate && (
                                        <li className="flex items-center text-gray-700">
                                            <FaCalendarAlt className="w-5 h-5 mr-3 text-arcdom-gold flex-shrink-0" />
                                            <span className="font-medium">Completion Date: </span>
                                            <span className="ml-2">{project.endDate}</span>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {isLightboxOpen && hasGallery && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-85 p-4"
                        onClick={closeLightbox}
                    >
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}
                            className="relative w-full max-w-5xl max-h-[90vh]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img src={project.gallery[activeIndex]} alt={`${project.title} ${activeIndex + 1}`} className="max-w-full max-h-[90vh] object-contain block mx-auto"/>
                            <button onClick={closeLightbox} className="absolute -top-2 -right-2 md:top-2 md:right-2 z-10 text-white bg-black bg-opacity-50 rounded-full p-1.5 hover:bg-opacity-75 transition-colors" aria-label="Close fullscreen image">
                                <MdClose size={28} />
                            </button>
                            {project.gallery.length > 1 && (
                                <>
                                    <button onClick={(e) => { e.stopPropagation(); handlePrevClick(); }} className="absolute top-1/2 left-1 md:left-3 transform -translate-y-1/2 z-10 bg-black bg-opacity-40 text-white hover:bg-opacity-60 rounded-full p-2 transition-colors duration-200" aria-label="Previous image"> <IoChevronBack size={28} /> </button>
                                    <button onClick={(e) => { e.stopPropagation(); handleNextClick(); }} className="absolute top-1/2 right-1 md:right-3 transform -translate-y-1/2 z-10 bg-black bg-opacity-40 text-white hover:bg-opacity-60 rounded-full p-2 transition-colors duration-200" aria-label="Next image"> <IoChevronForward size={28} /> </button>
                                </>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default ProjectDetailPage;
